import type { NodeKind, SearchGraphResponseEntry } from '@onefootprint/request-types/dashboard';
import Fuse from 'fuse.js';

export type SearchResult = {
  nodeId: string;
  nodeKind: NodeKind;
  nodeName?: string | null;
  matchingString: string;
  score: number;
};

type SearchItem = {
  nodeId: string;
  nodeKind: NodeKind;
  nodeName?: string | null;
  searchableString: string;
  tokenized: string;
};

export const MIN_SEARCH_LENGTH = 2;

export const createSearchIndex = (nodes: SearchGraphResponseEntry[]) => {
  const items: SearchItem[] = nodes.flatMap(node =>
    node.searchableStrings.map(str => ({
      nodeId: node.id,
      nodeKind: node.kind,
      nodeName: node.name,
      searchableString: str,
      // Tokenized: "id.first_name" -> "id first name"
      tokenized: str.replace(/[._]/g, ' '),
    })),
  );

  return new Fuse(items, {
    keys: ['searchableString', 'tokenized', 'nodeName'],
    threshold: 0.3,
    ignoreLocation: true,
    minMatchCharLength: 2,
    includeScore: true,
  });
};

export const searchNodes = (fuse: Fuse<SearchItem>, query: string): SearchResult[] => {
  if (!query || query.length < MIN_SEARCH_LENGTH) return [];

  const tokenizedQuery = query.replace(/[._]/g, ' ');
  const words = tokenizedQuery
    .trim()
    .split(/\s+/)
    .filter(w => w.length >= MIN_SEARCH_LENGTH);

  if (words.length === 0) return [];

  // For each query word, fuzzy-search the index and keep only results where
  // the word is a prefix of a token (eg "fi" matches "first" in "id first name").
  // This tracks 2 things in parallel:
  //   1. candidateNodeIds: nodes that contain at least one item matching every word (intersection)
  //   2. itemData: per-item scores and which words each item matched individually
  let candidateNodeIds: Set<string> | null = null;
  const itemData = new Map<string, { item: SearchItem; matchedWords: Set<string>; scores: number[] }>();

  for (const word of words) {
    const wordResults = fuse.search(word);
    const matchingResults = wordResults.filter(result => {
      const targetWords = result.item.tokenized.toLowerCase().split(' ');
      return targetWords.some(tw => tw.startsWith(word.toLowerCase()));
    });

    const nodeIdsForWord = new Set(matchingResults.map(r => r.item.nodeId));

    for (const result of matchingResults) {
      const key = `${result.item.nodeId}:${result.item.searchableString}`;
      const score = result.score ?? 1;
      const entry = itemData.get(key);
      if (entry) {
        entry.matchedWords.add(word);
        entry.scores.push(score);
      } else {
        itemData.set(key, { item: result.item, matchedWords: new Set([word]), scores: [score] });
      }
    }

    if (candidateNodeIds === null) {
      candidateNodeIds = nodeIdsForWord;
    } else {
      const intersection = new Set<string>();
      for (const id of candidateNodeIds) {
        if (nodeIdsForWord.has(id)) intersection.add(id);
      }
      candidateNodeIds = intersection;
    }
  }

  if (!candidateNodeIds || candidateNodeIds.size === 0) return [];

  // An item must belong to a candidate node AND match every query word itself.
  // This prevents partial matches from surfacing (eg "first_name" won't return "last_name", even if both are on the same node and both match "name").
  // Then dedupe to one row per node, keeping the best-scoring matching string — a node can expose the same field across multiple searchable strings (eg a branch expression plus its onboarding-data key) and we don't want the node to appear multiple times.
  const bestByNode = new Map<string, SearchResult>();
  for (const [, { item, matchedWords, scores }] of itemData) {
    if (!candidateNodeIds.has(item.nodeId)) continue;
    if (matchedWords.size < words.length) continue;

    const avgScore = scores.reduce((sum, s) => sum + s, 0) / scores.length;
    const existing = bestByNode.get(item.nodeId);
    if (!existing || avgScore < existing.score) {
      bestByNode.set(item.nodeId, {
        nodeId: item.nodeId,
        nodeKind: item.nodeKind,
        nodeName: item.nodeName,
        matchingString: item.searchableString,
        score: avgScore,
      });
    }
  }

  return [...bestByNode.values()].sort((a, b) => a.score - b.score);
};
